import { GridActionsCellItem, GridEditSingleSelectCell } from "@mui/x-data-grid";
import React from "react";
import { isDate, showDateOnly } from "../../Common/Utility";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import PdfIcon from "@mui/icons-material/PictureAsPdf";

function columnBuilder(props) {
  const {
    GridRowModes,
    rowModesModel,
    handlePdfJobCenterClick,
    handleEditClick,
    handleCancelClick,
    handleDeleteClick,
    handleSaveClick,
    branchData,
    regionData,
    activeUsers = [],
    onCheckPartnerPermission,
    onRevertPartnerName,
  } = props;

  const columns = [
    { field: "id", headerName: "ID", width: 75, editable: false },
    {
      field: "studentName",
      headerName: "Schüler/in *",
      type: "text",
      editable: true,
      width: 250,
    },
    {
      field: "address",
      headerName: "Adresse",
      type: "text",
      editable: true,
      width: 140,
    },
    {
      field: "zip",
      headerName: "PLZ",
      type: "number",
      editable: true,
      width: 100,
      valueFormatter: (params) => {
        if (!params?.value || params?.value === 0) return "";
        return `${params.value}`;
      },
      renderCell: (params) => {
        if (!params?.value || params?.value === 0) return "";
        return params.value;
      },
    },
    {
      field: "ort",
      headerName: "Ort",
      type: "text",
      editable: true,
      width: 120,
    },
    {
      field: "regionId",
      headerName: "Ort (Region) *",
      type: "singleSelect",
      editable: true,
      width: 120,
      getOptionValue: (region) => region?.id,
      getOptionLabel: (region) => region?.regionName,
      valueOptions: regionData || [],
      valueFormatter: (params) => {
        if (!params?.value || !regionData?.length) return "";
        const region = regionData.find((region) => region.id === params.value);
        return region?.regionName || "";
      },
      renderCell: (params) => {
        if (!params?.value || !regionData?.length) return "";
        const region = regionData.find((region) => region.id === params.value);
        return region?.regionName || "";
      },
    },
    {
      field: "branchId",
      headerName: "Abrechnungstelle *",
      type: "singleSelect",
      editable: true,
      width: 250,
      getOptionValue: (branch) => branch?.id,
      getOptionLabel: (branch) => branch?.branchName,
      valueOptions: branchData || [],
      valueFormatter: (params) => {
        if (!params?.value || !branchData?.length) return "";
        const branch = branchData.find((branch) => branch.id === params.value);
        return branch?.branchName || "";
      },
      renderCell: (params) => {
        if (!params?.value || !branchData?.length) return "";
        const branch = branchData.find((branch) => branch.id === params.value);
        return branch?.branchName || "";
      },
    },
    {
      field: "tel",
      headerName: "Telefon",
      type: "text",
      editable: true,
      width: 100,
    },
    {
      field: "birthDate",
      headerName: "Geburtsdatum *",
      type: "date",
      editable: true,
      width: 120,
      valueFormatter: (params) => {
        if (!params?.value) return "";
        return params.value ? showDateOnly(params.value) : "";
      },
      renderCell: (params) => {
        if (!params?.value) return "";
        return params.value ? showDateOnly(params.value) : "";
      },
      filterOperators: [
        {
          label: 'contains',
          value: 'contains',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return (params) => {
              // Check both the raw date value and the formatted date
              const rawDateValue = params.value ? String(params.value) : '';
              const formattedDateValue = params.value ? showDateOnly(params.value) : '';
              
              return rawDateValue.toLowerCase().includes(filterItem.value.toLowerCase()) || 
                     formattedDateValue.toLowerCase().includes(filterItem.value.toLowerCase());
            };
          },
        },
      ],
    },
    {
      field: "butNumber",
      headerName: "BuT-Nummer *",
      type: "text",
      editable: true,
      width: 170,
    },
    {
      field: "regionCenter",
      headerName: "RegionHannover",
      type: "boolean",
      editable: true,
      width: 120,
    },
    {
      field: "isPartner",
      headerName: "Partner",
      type: "boolean",
      editable: false,
      width: 120,
    },
    {
      field: "userName",
      headerName: "Partner Name",
      type: "singleSelect",
      editable: true,
      width: 150,
      getOptionValue: (item) => item?.userName,
      getOptionLabel: (item) => item?.userName ?? "",
      valueOptions: activeUsers || [],
      valueFormatter: (params) => {
        if (!params?.value) return "";
        if (!activeUsers?.length) return params.value;
        const item = activeUsers.find(
          (u) => (u?.userName ?? "").toLowerCase() === (params.value || "").toLowerCase()
        );
        return item?.userName || params.value || "";
      },
      renderCell: (params) => {
        if (!params?.value) return "";
        if (!activeUsers?.length) return params.value;
        const item = activeUsers.find(
          (u) => (u?.userName ?? "").toLowerCase() === (params.value || "").toLowerCase()
        );
        return item?.userName || params.value || "";
      },
      renderEditCell: (params) =>
        React.createElement(GridEditSingleSelectCell, {
          ...params,
          onValueChange:
            onCheckPartnerPermission &&
            (async (event, newValue) => {
              const row = params.row || {};
              const regionId = row.regionId ?? 0;
              const branchId = row.branchId ?? 0;
              if (!regionId || !branchId || !newValue) return;
              const allowed = await onCheckPartnerPermission(
                newValue,
                regionId,
                branchId
              );
              if (!allowed && onRevertPartnerName) {
                onRevertPartnerName(params.id, params.value);
              }
            }),
        }),
    },
    {
      field: "expiryDate",
      headerName: "Verfallsdatum",
      type: "date",
      editable: true,
      width: 120,
      valueFormatter: (params) => {
        if (!params?.value) return "";
        return params.value ? showDateOnly(params.value) : "";
      },
      renderCell: (params) => {
        if (!params?.value) return "";
        return params.value ? showDateOnly(params.value) : "";
      },
      filterOperators: [
        {
          label: 'contains',
          value: 'contains',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return (params) => {
              const dateValue = params.value ? showDateOnly(params.value) : '';
              return dateValue.toLowerCase().includes(filterItem.value.toLowerCase());
            };
          },
        },
      ],
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={
              <span style={{ color: "#d32f2f" }}>
                <PdfIcon />
              </span>
            }
            label="View"
            className="textPrimary"
            onClick={handlePdfJobCenterClick(id)}
            color="red"
          />,
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];
  return columns;
}
export default columnBuilder;
